/**
 * Call Off API module - endpoints for call-off management, stock planning,
 * part repository, and customer stock.
 */
import api, { type AxiosResponse } from './client';
import type {
  CallOff,
  CallOffStats,
  CallOffEditHistoryEntry,
  CallOffMessage,
  CallOffTimelineEvent,
  CallOffDelivery,
  CallOffLine,
  CreateCallOffRequest,
  BatchInlineEditChange,
  AvailablePartForCallOff,
  CallOffDeliveryDestination,
  StockPlanningResponse,
  ColumnPreference,
  ColumnOption,
  PartRepositoryResponse,
  PartCustomerAssignment,
  PartRepositoryStats,
  CustomerStockItem,
  StockChangeLogEntry,
  StockUploadResult,
  DirectDeliveryOrder,
  CallOffSupplierConfig,
  CallOffDirectDeliverySource,
  QueryParams,
} from './types';

export const callOffAPI = {
  // ---------------------------------------------------------------------------
  // Call-Off CRUD + Lifecycle
  // ---------------------------------------------------------------------------

  /** List call-offs with filters and pagination */
  getCallOffs: (params?: QueryParams): Promise<AxiosResponse<{ results: CallOff[]; count: number }>> =>
    api.get('/call-off/call-offs/', { params }),

  /** Get call-off details with lines */
  getCallOff: (id: number): Promise<AxiosResponse<CallOff>> =>
    api.get(`/call-off/call-offs/${id}/`),

  /** Create a new call-off */
  createCallOff: (data: CreateCallOffRequest): Promise<AxiosResponse<CallOff>> =>
    api.post('/call-off/call-offs/', data),

  /** Update a call-off */
  updateCallOff: (id: number, data: Partial<CreateCallOffRequest>): Promise<AxiosResponse<CallOff>> =>
    api.patch(`/call-off/call-offs/${id}/`, data),

  /** Send call-off to customer (draft → pending) */
  sendCallOff: (id: number, skipValidation?: boolean): Promise<AxiosResponse<CallOff>> =>
    api.post(`/call-off/call-offs/${id}/send/`, { skip_validation: skipValidation }),

  /** Confirm call-off (pending → confirmed) */
  confirmCallOff: (id: number): Promise<AxiosResponse<CallOff>> =>
    api.post(`/call-off/call-offs/${id}/confirm/`),

  /** Dispatch call-off (confirmed → dispatched) */
  dispatchCallOff: (id: number): Promise<AxiosResponse<CallOff>> =>
    api.post(`/call-off/call-offs/${id}/dispatch/`),

  /** Mark call-off as delivered (dispatched → delivered) */
  deliverCallOff: (id: number): Promise<AxiosResponse<CallOff>> =>
    api.post(`/call-off/call-offs/${id}/deliver/`),

  /** Cancel call-off (draft/pending → cancelled) */
  cancelCallOff: (id: number): Promise<AxiosResponse<CallOff>> =>
    api.post(`/call-off/call-offs/${id}/cancel/`),

  /** Bulk confirm multiple call-offs */
  bulkConfirm: (callOffIds: number[]): Promise<AxiosResponse<{ confirmed: number; errors: Record<string, unknown>[] }>> =>
    api.post('/call-off/call-offs/bulk-confirm/', { call_off_ids: callOffIds }),

  /** Get call-off status statistics */
  getCallOffStats: (): Promise<AxiosResponse<CallOffStats>> =>
    api.get('/call-off/call-offs/stats/'),

  /** Get call-off timeline events */
  getTimeline: (id: number): Promise<AxiosResponse<CallOffTimelineEvent[]>> =>
    api.get(`/call-off/call-offs/${id}/timeline/`),

  /** Get call-off edit history */
  getEditHistory: (id: number): Promise<AxiosResponse<CallOffEditHistoryEntry[]>> =>
    api.get(`/call-off/call-offs/${id}/edit-history/`),

  /** Get call-off messages */
  getMessages: (id: number): Promise<AxiosResponse<CallOffMessage[]>> =>
    api.get(`/call-off/call-offs/${id}/messages/`),

  /** Send a message on a call-off */
  sendMessage: (id: number, messageText: string): Promise<AxiosResponse<CallOffMessage>> =>
    api.post(`/call-off/call-offs/${id}/messages/`, { message_text: messageText }),

  /** Mark messages from the other party as read */
  markMessagesRead: (id: number): Promise<AxiosResponse<{ marked_read: number }>> =>
    api.post(`/call-off/call-offs/${id}/mark-read/`),

  /** Inline edit a call-off line field */
  inlineEdit: (id: number, lineId: number, field: string, value: string): Promise<AxiosResponse<{ status: string }>> =>
    api.post(`/call-off/call-offs/${id}/inline-edit/`, { line_id: lineId, field, value }),

  /** Batch inline edit multiple lines (customer flow) */
  batchInlineEdit: (id: number, changes: BatchInlineEditChange[]): Promise<AxiosResponse<{ status: string; changes_applied: number }>> =>
    api.post(`/call-off/call-offs/${id}/batch-inline-edit/`, { changes }),

  /** Add new lines to a call-off */
  addLines: (id: number, lines: Record<string, unknown>[]): Promise<AxiosResponse<CallOffLine[]>> =>
    api.post(`/call-off/call-offs/${id}/add-lines/`, { lines }),

  /** Get available parts for the call-off's customer */
  getAvailableParts: (id: number): Promise<AxiosResponse<AvailablePartForCallOff[]>> =>
    api.get(`/call-off/call-offs/${id}/available-parts/`),

  /** Get PO availability per warehouse part on this call-off.
   *  Scoped to call-off's supplier/customer, so customers get correct data. */
  getCallOffAvailability: (id: number): Promise<AxiosResponse<Record<string, string>>> =>
    api.get(`/call-off/call-offs/${id}/availability/`),

  /** Get delivery destinations for a call-off */
  getAvailableDeliveries: (id: number): Promise<AxiosResponse<CallOffDeliveryDestination[]>> =>
    api.get(`/call-off/call-offs/${id}/available-deliveries/`),

  /** Get delivery records (ASNs) for a call-off */
  getDeliveries: (id: number): Promise<AxiosResponse<CallOffDelivery[]>> =>
    api.get(`/call-off/call-offs/${id}/deliveries/`),

  /** Get customers with assigned parts */
  getCustomers: (): Promise<AxiosResponse<{ id: number; name: string; customer_code: string }[]>> =>
    api.get('/call-off/call-offs/customers/'),

  /** Get customer statistics */
  getCustomerStats: (): Promise<AxiosResponse<{ customer__id: number; customer__name: string; total: number; active: number }[]>> =>
    api.get('/call-off/call-offs/customer-stats/'),

  /** Get available warehouses */
  getWarehouses: (): Promise<AxiosResponse<{ id: number; name: string; code: string; warehouse_type: string }[]>> =>
    api.get('/call-off/call-offs/warehouses/'),

  /** Get direct delivery orders (grouped PolishFactoryItems by sales_order) */
  getDirectDeliveryOrders: (params?: QueryParams): Promise<AxiosResponse<DirectDeliveryOrder[]>> =>
    api.get('/call-off/direct-delivery-orders/', { params }),

  // ---------------------------------------------------------------------------
  // Stock Planning
  // ---------------------------------------------------------------------------

  /** Get stock planning grid data */
  getStockPlanning: (params?: QueryParams): Promise<AxiosResponse<StockPlanningResponse>> =>
    api.get('/call-off/stock-planning/', { params }),

  /** Get all available column definitions */
  getColumnOptions: (): Promise<AxiosResponse<ColumnOption[]>> =>
    api.get('/call-off/stock-planning/column-options/'),

  /** Get user's column preferences */
  getColumns: (): Promise<AxiosResponse<ColumnPreference[]>> =>
    api.get('/call-off/stock-planning/columns/'),

  /** Toggle column visibility */
  toggleColumn: (columnId: string): Promise<AxiosResponse<ColumnPreference>> =>
    api.post('/call-off/stock-planning/toggle-column/', { column_id: columnId }),

  /** Update column sort order */
  sortColumns: (columns: { column_id: string; sort_order: number }[]): Promise<AxiosResponse<ColumnPreference[]>> =>
    api.post('/call-off/stock-planning/sort-columns/', { columns }),

  // ---------------------------------------------------------------------------
  // Part Repository
  // ---------------------------------------------------------------------------

  /** List parts with customer assignments */
  getParts: (params?: QueryParams): Promise<AxiosResponse<PartRepositoryResponse>> =>
    api.get('/call-off/parts/', { params }),

  /** Get part details */
  getPart: (id: number): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.get(`/call-off/parts/${id}/`),

  /** Update part details */
  updatePart: (id: number, data: Record<string, unknown>): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.patch(`/call-off/parts/${id}/`, data),

  /** Get customers assigned to a part */
  getPartCustomers: (id: number): Promise<AxiosResponse<PartCustomerAssignment[]>> =>
    api.get(`/call-off/parts/${id}/customers/`),

  /** Assign customers to a part (replacement) */
  assignPartCustomers: (id: number, customerIds: number[]): Promise<AxiosResponse<{ status: string }>> =>
    api.post(`/call-off/parts/${id}/customers/`, { customer_ids: customerIds }),

  /** Clear all customers from a part */
  clearPartCustomers: (id: number): Promise<AxiosResponse<void>> =>
    api.delete(`/call-off/parts/${id}/customers/`),

  /** Bulk assign customers to multiple parts */
  bulkAssignCustomers: (partIds: number[], customerIds: number[]): Promise<AxiosResponse<{ created: number }>> =>
    api.post('/call-off/parts/bulk-assign/', { part_ids: partIds, customer_ids: customerIds }),

  /** Get part repository statistics */
  getPartStats: (): Promise<AxiosResponse<PartRepositoryStats>> =>
    api.get('/call-off/parts/stats/'),

  // ---------------------------------------------------------------------------
  // Customer Stock
  // ---------------------------------------------------------------------------

  /** List customer stock levels */
  getCustomerStock: (params?: QueryParams): Promise<AxiosResponse<{ items: CustomerStockItem[]; total_count: number; page: number; page_size: number }>> =>
    api.get('/call-off/customer-stock/', { params }),

  /** Save inline stock edit. supplier_id is optional — backend resolves it
   * for customer users via PartCustomerAssignment. */
  saveStock: (data: { item_code: string; quantity: number; supplier_id?: number }): Promise<AxiosResponse<{ status: string }>> =>
    api.post('/call-off/customer-stock/save/', data),

  /** Download stock upload template */
  downloadTemplate: (supplierId?: number): Promise<AxiosResponse<Blob>> =>
    api.get('/call-off/customer-stock/template/', { params: supplierId ? { supplier_id: supplierId } : {}, responseType: 'blob' }),

  /** Upload customer stock Excel file */
  uploadStock: (data: FormData): Promise<AxiosResponse<StockUploadResult>> =>
    api.post('/call-off/customer-stock/upload/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),

  /** Bulk save multiple stock edits (Save Changes button) */
  bulkSaveStock: (data: { items: Array<{ item_code: string; quantity: number }> }): Promise<AxiosResponse<{ saved: number; errors: string[] }>> =>
    api.post('/call-off/customer-stock/bulk-save/', data),

  /** Export stock levels to Excel (.xlsx) or CSV */
  exportStock: (format: 'xlsx' | 'csv' = 'xlsx'): Promise<AxiosResponse<Blob>> =>
    api.get('/call-off/customer-stock/export/', { params: { file_format: format }, responseType: 'blob' }),

  /** Get stock change history */
  getChangeHistory: (params?: QueryParams): Promise<AxiosResponse<{ items: StockChangeLogEntry[]; total_count: number; page: number; page_size: number }>> =>
    api.get('/call-off/customer-stock/change-history/', { params }),

  // ---------------------------------------------------------------------------
  // Sub-tabs
  // ---------------------------------------------------------------------------

  /** Get stock status list */
  getStockStatus: (params?: QueryParams): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.get('/call-off/stock-status/', { params }),

  /** Get open PO list */
  getOpenPO: (params?: QueryParams): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.get('/call-off/open-po/', { params }),

  /** Get SAL committed list */
  getSalCommitted: (params?: QueryParams): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.get('/call-off/sal-committed/', { params }),

  /** Get Polish Factory list */
  getPolishFactory: (params?: QueryParams): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.get('/call-off/polish-factory/', { params }),

  /** Get min/max stock levels */
  getMinMax: (params?: QueryParams): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.get('/call-off/min-max/', { params }),

  /** Get forecast list */
  getForecasts: (params?: QueryParams): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.get('/call-off/forecasts/', { params }),

  /** Get Poland stock list */
  getPolandStock: (params?: QueryParams): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.get('/call-off/poland-stock/', { params }),

  /** Get in-transit list */
  getInTransit: (params?: QueryParams): Promise<AxiosResponse<Record<string, unknown>>> =>
    api.get('/call-off/in-transit/', { params }),

  // ---------------------------------------------------------------------------
  // Supplier Configuration (templating)
  // ---------------------------------------------------------------------------

  /** Get the active Call-Off configuration for the current user's supplier.
   *  Always returns a payload — defaults match historical hardcoded values
   *  when no row exists. */
  getSupplierConfig: (): Promise<AxiosResponse<CallOffSupplierConfig>> =>
    api.get('/call-off/supplier-config/current/'),

  /** Helix-admin only: list all supplier configs */
  listSupplierConfigs: (): Promise<AxiosResponse<{ results: CallOffSupplierConfig[]; count: number }>> =>
    api.get('/call-off/supplier-config/'),

  /** Helix-admin only: update a supplier config */
  updateSupplierConfig: (id: number, data: Partial<CallOffSupplierConfig>): Promise<AxiosResponse<CallOffSupplierConfig>> =>
    api.patch(`/call-off/supplier-config/${id}/`, data),

  /** Helix-admin only: add a Direct Delivery source */
  addDirectDeliverySource: (configId: number, data: Partial<CallOffDirectDeliverySource>): Promise<AxiosResponse<CallOffDirectDeliverySource>> =>
    api.post(`/call-off/supplier-config/${configId}/sources/`, data),

  /** Helix-admin only: update a Direct Delivery source */
  updateDirectDeliverySource: (configId: number, sourceId: number, data: Partial<CallOffDirectDeliverySource>): Promise<AxiosResponse<CallOffDirectDeliverySource>> =>
    api.patch(`/call-off/supplier-config/${configId}/sources/${sourceId}/`, data),

  /** Helix-admin only: delete a Direct Delivery source */
  deleteDirectDeliverySource: (configId: number, sourceId: number): Promise<AxiosResponse<void>> =>
    api.delete(`/call-off/supplier-config/${configId}/sources/${sourceId}/`),

  /** Helix-admin only: reorder Direct Delivery sources by ID */
  reorderDirectDeliverySources: (configId: number, order: number[]): Promise<AxiosResponse<CallOffSupplierConfig>> =>
    api.post(`/call-off/supplier-config/${configId}/sources/reorder/`, { order }),
};
